/**
 * English dictionary — source of truth. Keys nest by page/component so a
 * missing translation in mr.ts is easy to locate. Server-driven data (project/
 * zone/ward/block names, surveyor names, house numbers) is never routed
 * through this dictionary — see CLAUDE.md §2.2 and the i18n plan.
 */
export const en = {
  common: {
    cancel: "Cancel",
    delete: "Delete",
    deleting: "Deleting…",
    loading: "Loading",
    signOut: "Sign out",
    dismiss: "Dismiss",
    retry: "Retry",
    save: "Save",
    saving: "Saving…",
    add: "Add",
    copy: "Copy",
    copied: "Copied",
    preparing: "Preparing…",
    exportAgain: "Export again",
    exportShapefile: "Export Shapefile",
    undo: "Undo",
    discard: "Discard",
    done: "Done",
    edit: "Edit",
  },

  status: {
    block: {
      pending: "Not started",
      in_progress: "In progress",
      submitted: "Submitted",
    },
    raster: {
      receiving: "Uploading",
      uploaded: "Queued",
      processing: "Converting",
      ready: "Ready",
      failed: "Failed",
    },
  },

  ui: {
    hidePassword: "Hide password",
    showPassword: "Show password",
    surveyProgress: "Survey progress",
  },

  topBar: {
    signOut: "Sign out",
    language: "Language",
    breadcrumb: "Breadcrumb",
  },

  sidebar: {
    brandSubtitle: "Survey Operations",
    workspace: "Workspace",
    projects: "Projects",
    administration: "Administration",
    accounts: "Accounts",
    sessions: "Phones & sessions",
    reopenRequests: "Reopen requests",
    systemAccess: "System Access",
    signedIn: "Signed in",
    expandMenu: "Expand menu",
    collapseMenu: "Collapse menu",
  },

  appShell: {
    checkingSession: "Checking session…",
  },

  login: {
    brandSubtitle: "Survey operations",
    title: "Sign in",
    subtitle: "Office and field accounts both sign in here.",
    username: "Username",
    password: "Password",
    signingIn: "Signing in…",
    signIn: "Sign in",
    cantReachServer: "Can't reach the server. Check that the backend is running.",
    signInFailed: "Sign-in failed",
    signedOutReason: "You were signed out:",
  },

  projectsPage: {
    heading: "Projects",
    subheading: "Every orthomosaic and field-collected shape belongs to a project.",
    zones: "Zones",
    wards: "Wards",
    blocks: "Blocks",
    complete: "Complete",
    submittedOfTotal: (submitted: number, total: number) => `${submitted} of ${total} submitted`,
    allProjects: (count: number) => `All projects · ${count}`,
    filterPlaceholder: "Filter by name…",
    loadingProjects: "Loading projects…",
    noProjectsYet: "No projects yet",
    noMatches: "No matches",
    noProjectsBody:
      "Create your first project to start uploading drone imagery and defining survey areas.",
    noMatchesBody: "No project matches that filter.",
    colProject: "Project",
    colProgress: "Progress",
    colDescription: "Description",
    colCreated: "Created",
    colActions: "Actions",
    active: "Active",
    percentComplete: (pct: number) => `${pct}% complete`,
    zeroBlocks: "0 blocks",
    open: "Open",
    zonesAction: "Zones",
    deleteProjectTitle: "Delete this project?",
    deleteProjectBodySuffix:
      " and everything under it — every zone, ward and block, all uploaded imagery, and every shape surveyors have collected — is permanently removed. This is the most destructive action in the portal and cannot be undone.",
    deleteProjectConfirmLabel: "Delete project",
    exportedCanDelete: "Exported. You can now delete.",
    exportRequired: "Export required before deleting.",
    couldntDeleteProject: "Couldn't delete the project.",
    couldntReachBackend: "Couldn't reach the backend. Is it running on port 8300?",
    propertyProgress: "Property survey progress",
    propertiesTotal: (n: number) => `${n} properties`,
    surveyed: "Surveyed",
    notStarted: "Not started",
    byWard: "Ward-wise",
    byBlock: "Block-wise",
    colWard: "Ward",
    colBlock: "Block",
    noProperties: "No property outlines imported or collected yet.",
    progressFailed: "Couldn't load property progress. Restart the backend if it was started before this update.",
    loadingProgress: "Loading progress…",
    overview: "Portfolio overview",
    kpiProjects: "Projects",
    kpiProjectsHint: (active: number) => `${active} with survey work started`,
    kpiBlocks: "Blocks",
    kpiBlocksHint: (zones: number, wards: number) => `${zones} zones · ${wards} wards`,
    kpiSubmitted: "Blocks submitted",
    kpiSubmittedHint: (pct: string) => `${pct} of all blocks`,
    kpiInProgress: "Blocks in progress",
    kpiInProgressHint: (n: number) => `${n} not started`,
    colStatus: "Status",
    colHierarchy: "Zones · wards · blocks",
    statusNotStarted: "Not started",
    statusInProgress: "In progress",
    statusComplete: "Complete",
    statusNoBlocks: "No blocks yet",
    legendSubmitted: "Submitted",
    legendInProgress: "In progress",
    legendNotStarted: "Not started",
    blocksProgress: (submitted: number, total: number, pct: number) => `${submitted}/${total} · ${pct}%`,
    moreActions: (name: string) => `More actions for ${name}`,
    hierarchyAction: "Zones, wards & blocks",
    deleteAction: "Delete project…",
    deleting: "Deleting…",
    selectedProject: "Selected project",
    openWorkspace: "Open workspace",
    selectHint: "Click a row to show its details below.",
    sortBy: (col: string) => `Sort by ${col}`,
    searchLabel: "Search projects",
  },

  hierarchyPage: {
    crumbZonesWardsBlocks: "Zones, wards & blocks",
    zonesTitle: "Zones",
    wardsTitle: "Wards",
    blocksTitle: "Blocks",
    zonePlaceholder: "e.g. Zone 4",
    wardPlaceholder: "e.g. Ward 12",
    blockPlaceholder: "e.g. Block 5",
    selectZoneFirst: "Select a zone first",
    selectWardFirst: "Select a ward first",
    noneYet: "None yet — add one below.",
    noBoundaryGeometry: "No boundary geometry",
    noShape: "no shape",
    blocksInWard: "Blocks in ward",
    submitted: "Submitted",
    inProgress: "In progress",
    notStarted: "Not started",
    completedSurveys: (count: number) => `Completed surveys · ${count}`,
    nothingSubmittedYet: "Nothing submitted yet",
    nothingSubmittedBody:
      "When a surveyor submits a block from the field app, it appears here with who submitted it and when.",
    colBlock: "Block",
    colSubmittedBy: "Submitted by",
    colSubmitted: "Submitted",
    colClaimedBy: "Claimed by",
    colShapes: "Shapes",
    claimedBy: (name: string) => `claimed by ${name}`,
    noBoundaryWarning:
      "No boundary — the field app can't scope an offline download for this block",
    submittedCanEdit: "Submitted — you can still edit it here",
    surveyLocked: "Survey data is locked",
    reopening: "Reopening…",
    sendBackToSurveyor: "Send back to surveyor",
    sendBackTitle:
      "Return this block to the surveyor for rework. They cannot see a submitted block.",
    featureTableLockedReason: "This block is submitted. Ask the office to make corrections.",
    reopenedNoticeInProgress: (surveyor: string) => `, with ${surveyor}`,
    reopenedNoticePrefix: (blockName: string) => `${blockName} is back with the surveyor (now `,
    reopenedNoticeInProgressState: "in progress",
    reopenedNoticePendingState:
      "not started — it had no claim, so anyone can pick it up",
    reopenedNoticeSuffix: "). They will submit it again when the rework is done.",
  },

  usersPage: {
    crumbAccounts: "Accounts",
    crumbCreation: "Creation",
    crumbManaging: "Managing",
    headingCreate: "Account Creation",
    headingManage: "Account Management",
    subheadingCreate: "Provision credentials for surveyors and office staff.",
    subheadingManage: "Search, inspect, and configure credentials and assigned survey areas.",
    tabManaging: "Managing",
    tabCreation: "Creation",
    searchPlaceholder: "Search by username or name…",
    roleLabel: "Role:",
    projectLabel: "Project:",
    allRoles: "All Roles",
    roleSurveyor: "Surveyor",
    roleStaff: "Staff",
    roleAdmin: "Admin",
    allProjects: "All Projects",
    newAccount: "New account",
    accountsTitle: (shown: number, total: number | null) =>
      total !== null && shown !== total
        ? `Accounts (${shown} of ${total})`
        : `Accounts (${shown})`,
    accountsTitlePlain: "Accounts",
    loadingAccounts: "Loading accounts…",
    noAccountsYet: "No accounts created yet. Use the Creation tab to create the first one.",
    noAccountsMatch: "No accounts match the current search or role filter.",
    you: "(you)",
    usernameLabel: "Username:",
    password: "Password",
    progress: "Progress",
    assignment: "Assignment",
    allAccessAdmin: "All access (Admin)",
    delete: "Delete",
    assignSurveyAreasTitle: "Assign Survey Areas",
    assignSurveyAreasDescriptionPrefix: "Configure project, zone, ward, and block grants for",
    loadingGrants: "Loading existing grants and survey hierarchy…",
    changePasswordTitle: "Change Password",
    changePasswordDescriptionPrefix: "Set a new password for",
    deleteAccountTitle: "Delete this account?",
    deleteAccountBodyPrefix: "Delete",
    deleteAccountBodySuffix:
      "? They will no longer be able to log in to the field app or portal. Survey shapes they previously submitted remain preserved.",
    deleteAccountConfirmLabel: "Delete account",
    newAccountPanelTitle: "New account",
    fieldUsernameLabel: "Username",
    fieldUsernameHint: "Used to sign in. Lowercase, no spaces.",
    fieldFullNameLabel: "Full name",
    fieldFullNameHint: "Optional. Display name shown across the portal.",
    fieldPasswordLabel: "Password",
    fieldPasswordHint: "Share directly with the user; passwords cannot be viewed later.",
    passwordPlaceholder: "At least 8 characters",
    weakPassword: "Use at least 8 characters.",
    fieldRoleLabel: "Role",
    fieldRoleHint: "Choose authority level. Detailed comparison matrix is shown on the right.",
    fieldInitialProjectsLabel: "Initial Projects",
    fieldInitialProjectsHint:
      "Which campaigns this account can immediately access. You can refine specific blocks later.",
    creatingAccount: "Creating…",
    createAccount: "Create account",
    roleMatrixTitle: "What each role can do",
    roleMatrixIntro:
      "Permissions are enforced strictly server-side by FastAPI dependencies. A role cannot perform actions outside this matrix through the portal, mobile app, or API.",
    colCapability: "Capability",
    colSurveyor: "Surveyor",
    colStaff: "Staff",
    colAdmin: "Admin",
    rolePrinciplesTitle: "Role Principles",
    rolePrincipleStaffAdminLabel: "Staff vs Admin:",
    rolePrincipleStaffAdmin:
      "Equal in all GIS operations (imagery upload, vector editing, export). Admins additionally have authority to manage other admin logins.",
    rolePrincipleFieldLabel: "Field Surveys:",
    rolePrincipleField:
      "Surveyors only see blocks explicitly granted to them. Once a block is submitted, it is protected from further mobile edits unless reopened by staff.",
    rolePrincipleFailClosedLabel: "Fail-closed default:",
    rolePrincipleFailClosed:
      "Accounts created without any project assignment cannot access any data until assigned.",
    loadingProjects: "Loading projects…",
    noProjectsYetCreateOne: "No projects exist yet. Create one first, then assign it here.",
    selectedOfTotal: (selected: number, total: number) => `${selected} of ${total} selected`,
    clearAll: "Clear all",
    selectAll: "Select all",
    everythingAdmin: "Everything",
    adminSuffix: "(admin)",
    nothingAssigned: "Nothing assigned — this account can't see anything",
    setPassword: "Set password",
    newPasswordPlaceholder: "New password",
    createdAdminNotice: (username: string) =>
      `Created ${username} — an administrator with access to all projects.`,
    createdNoAssignmentNotice: (username: string) =>
      `Created ${username}. Use "Assignment" in Managing to assign survey areas.`,
    createdWithAssignmentsNotice: (username: string, count: number) =>
      `Created ${username} with ${count} project assignment(s).`,
    usernameAlreadyTaken: "That username is already taken.",
    onlyStaffCanCreate: "Only staff accounts can create new accounts.",
    deletedAccountNotice: (username: string) => `Deleted account "${username}".`,
    passwordUpdatedNotice: (username: string) =>
      `Password updated for "${username}". Give them the new password directly.`,
    noAssignmentsNotice: (username: string) =>
      `${username} has no assignments and cannot access any survey areas.`,
    updatedAssignmentsNotice: (username: string, count: number) =>
      `Updated assignments for ${username} (${count} grant${count === 1 ? "" : "s"}).`,
    zoneCount: (n: number) => `${n} zone${n === 1 ? "" : "s"}`,
    wardCount: (n: number) => `${n} ward${n === 1 ? "" : "s"}`,
    blockCount: (n: number) => `${n} block${n === 1 ? "" : "s"}`,
    roleMatrix: {
      seeBlocks: "See blocks",
      seeSubmittedData: "See submitted survey data",
      claimBlock: "Claim a block",
      collectEditShapes: "Collect / edit shapes",
      submitBlock: "Submit a block",
      editSubmittedBlock: "Edit a submitted block's data",
      reopen: "Reopen (send back for rework)",
      exportShapefile: "Export shapefile",
      hierarchyManage: "Projects / zones / wards / blocks",
      imageryManage: "Drone imagery + property outlines",
      whichProjects: "Which projects",
      manageAccounts: "Manage accounts",
      assignProjects: "Assign projects to accounts",
      surveyorSeeBlocks: "Pending + in-progress",
      staffSeeBlocks: "All, incl. submitted",
      adminSeeBlocks: "All, incl. submitted",
      surveyorSeeSubmitted: "No",
      staffSeeSubmitted: "Yes",
      adminSeeSubmitted: "Yes",
      surveyorClaim: "Unclaimed only",
      staffClaim: "Opens claimed ones read-only",
      adminClaim: "Opens claimed ones read-only",
      surveyorCollect: "In blocks they hold",
      staffCollect: "Yes",
      adminCollect: "Yes",
      surveyorSubmit: "Own claimed block",
      staffSubmit: "Any block",
      adminSubmit: "Any block",
      surveyorEditSubmitted: "No",
      staffEditSubmitted: "Yes, in place",
      adminEditSubmitted: "Yes, in place",
      surveyorReopen: "No",
      staffReopen: "Yes",
      adminReopen: "Yes",
      surveyorExport: "Single block only",
      staffExport: "Whole project",
      adminExport: "Whole project",
      surveyorHierarchy: "Read only",
      staffHierarchy: "Create + delete",
      adminHierarchy: "Create + delete",
      surveyorImagery: "No access",
      staffImagery: "Upload, register, delete",
      adminImagery: "Upload, register, delete",
      surveyorWhichProjects: "Only those assigned",
      staffWhichProjects: "Only those assigned",
      adminWhichProjects: "All, always",
      surveyorManageAccounts: "No",
      staffManageAccounts: "Surveyors + staff",
      adminManageAccounts: "Anyone, incl. other admins",
      surveyorAssignProjects: "No",
      staffAssignProjects: "Yes",
      adminAssignProjects: "Yes",
    },
  },

  projectWorkspacePage: {
    imageryAndFieldData: "Project-level imagery and field data.",
    imageryReadyOfTotal: (ready: number, total: number) => `${ready} of ${total} imagery file`,
    imageryReadySuffix: (total: number): string => (total === 1 ? " ready." : "s ready."),
    zonesWardsBlocks: "Zones, wards & blocks",
    surveyTiming: "Survey timing",
    hideUploadPanels: "Hide upload panels",
    showUploadPanels: "Show upload panels",
    droneImagery: "Drone imagery",
    propertyOutlines: "Property outlines",
    editableLayers: "Editable layers",
    exportScopeNote: "Export covers the whole project, not this filter.",
    qaChecking: "Checking properties for overlaps and slivers…",
    qaNone: (checked: number) => `No overlaps or slivers found in ${checked} properties.`,
    qaOverlaps: "overlaps",
    qaSlivers: "slivers",
    qaCheckedOf: (checked: string) => `in ${checked} properties checked`,
    qaOverlap: "Overlap",
    qaSliver: "Sliver",
    qaPercentOfSmaller: (pct: number) => `${pct}% of smaller`,
    qaVeryThin: "very thin",
    qaVerySmall: "very small",
    qaFailed: "Couldn't run the overlap & sliver check.",
    qaShowList: "Show list",
    qaHideList: "Hide list",
    qaListCapped: (shown: number) => `Showing the first ${shown}.`,
    qaZoom: "Zoom",
    qaClose: "Turn off check",
  },

  hierarchyImportPanel: {
    panelTitle: "Import from GeoJSON",
    intro:
      "Upload a GeoJSON per level to build this project's hierarchy. Wards and blocks are attached to whichever zone or ward they sit inside, so the files need no matching ID columns. A projected file (e.g. UTM) is converted automatically when it declares its CRS.",
    levelZone: "Zones",
    levelWard: "Wards",
    levelBlock: "Blocks",
    hintZone: "Import first",
    hintWard: "Placed inside zones",
    hintBlock: "Placed inside wards",
    chooseFile: "Choose file",
    importing: "Importing…",
    removing: "Removing…",
    removeAll: (count: number, label: string) => `Remove all ${count} ${label}`,
    uploadsTitle: "Uploads",
    loadingUploads: "Loading…",
    noUploadsRecorded:
      "No uploads recorded yet. Undo is offered for a file you import here, and only until you leave this page — after that, “Remove all” above is what removes an imported level.",
    undoOfferNote: "Undo is offered for what you imported just now, and only until you leave this page.",
    onlyGeojsonAccepted: "Only .geojson / .json files are accepted.",
    couldntImport: (levelLabel: string) => `Couldn't import ${levelLabel}.`,
    couldntRemove: (label: string) => `Couldn't remove ${label}.`,
    serverStillRefused: "Server still refused the removal.",
    removedNothingAlreadyEmpty: "nothing — already empty",
    removedSummary: (parts: string) => `Removed ${parts}`,
    zonesCount: (n: number) => `${n} zone(s)`,
    wardsCount: (n: number) => `${n} ward(s)`,
    blocksCount: (n: number) => `${n} block(s)`,
    exportFailedGeneric: "Export failed.",
    undoThisUpload: (name: string) => `Undo ${name}?`,
    thisUpload: "this upload",
    removeAllQuestion: (label: string) => `Remove all ${label}?`,
    undoBodyTemplate: (count: number, level: string) =>
      `Removes the ${count} ${level}(s) this upload created, leaving every other upload in place.`,
    removeBodyTemplate: (label: string) => `Every imported ${label} in this project is removed.`,
    cascadeZone: " Every ward and block goes with them.",
    cascadeWard: " Every block goes with them.",
    reimportToRestore: " You would re-import the file to get them back.",
    undoImportLabel: "Undo import",
    removeLabel: "Remove",
    destroysFieldWorkTitle: "This would destroy collected field work",
    destroysFieldWorkBody1: "This permanently deletes",
    destroysFieldWorkBody2: "collected shape(s)",
    destroysFieldWorkBody3: "across",
    destroysFieldWorkBody4: "block(s)",
    destroysFieldWorkBody5: "already started or submitted",
    destroysFieldWorkImagery: (count: number) => `, plus ${count} imagery file(s)`,
    destroysFieldWorkBody6:
      ". A shape a surveyor walked to and drew cannot be recreated from anything the office holds — export it first.",
    deleteAnyway: "Delete anyway",
    exportedCanRemove: "Exported. You can now remove.",
    exportFieldDataFirst: "Export the field data first.",
    exportedCanDelete: "Exported. You can now delete.",
    exportRequiredDelete: "Export required before deleting.",
    exportNoteWholeProject:
      "Downloads every shape in this project, not just the ones under this level — the removal clears the project's blocks either way.",
    alreadyHasTitle: (existing: number, label: string) =>
      `This project already has ${existing} ${label}`,
    importAddsPrefix: "Importing",
    importAddsAddsTo: "adds to",
    importAddsSuffix:
      "them rather than replacing them — nothing is matched up or overwritten, so re-importing the same file produces a duplicate of every row, names included. To replace instead, remove the existing",
    importAddsFirst: "first.",
    importAnyway: "Import anyway",
    createdCount: (created: number, level: string) =>
      `${created} ${level}${created === 1 ? "" : "s"} created`,
    skippedCount: (n: number) => `${n} skipped`,
    placedByFallback: (n: number) => `${n} placed by fallback`,
    reprojectedFromCrs: (crs: string) => `reprojected from ${crs}`,
  },

  shapeUpdate: {
    title: "Correct shapes (keeps field data)",
    intro:
      "Export a level with its ids, edit shapes, names or parent (zone_id / ward_id) in GIS, then update from the edited file. Rows are matched by gs_id, or by parent + name; nothing is created or deleted.",
    exportLevel: (label: string) => `Export ${label}`,
    updateLevel: (label: string) => `Update ${label}…`,
    exporting: "Exporting…",
    checking: "Checking file…",
    previewTitle: (label: string, file: string) => `Update ${label} from ${file}?`,
    summary: (r: { reshaped: number; renamed: number; moved: number; unchanged: number; skipped: number; untouched: number }) =>
      `${r.reshaped} reshaped · ${r.renamed} renamed · ${r.moved} moved · ${r.unchanged} unchanged · ${r.skipped} skipped · ${r.untouched} not in file (left as they are)`,
    nothingToApply: "Nothing in this file differs from what is stored.",
    outsideWarning: (n: number) =>
      `${n} field-data shape${n === 1 ? " is" : "s are"} no longer inside the new outline of ${n === 1 ? "its" : "their"} block, and no other block covers ${n === 1 ? "it" : "them"}. They stay in their block, but surveyors can't edit them until an outline covers them.`,
    movedInfo: (n: number) =>
      `${n} propert${n === 1 ? "y" : "ies"} will move to the block that now contains ${n === 1 ? "it" : "them"} (ward and zone follow). Undo puts ${n === 1 ? "it" : "them"} back.`,
    movedRow: (house: string | null, from: string, to: string) =>
      `${house ? `No. ${house}` : "Unnumbered"}: ${from} → ${to}`,
    toSubmitted: "submitted block",
    colName: "Name",
    colMatchedBy: "Matched by",
    colChange: "Change",
    changeShape: "shape",
    changeName: (from: string) => `name (was ${from})`,
    changeMove: (from: string | null, to: string | null) => `moved ${from ?? "?"} → ${to ?? "?"}`,
    outsideCount: (n: number) => `${n} outside`,
    skippedTitle: "Skipped",
    skippedRow: (n: number, name: string | null, reason: string) => `Feature ${n}${name ? ` (${name})` : ""}: ${reason}`,
    listCapped: "Only the first 300 are listed.",
    apply: (n: number) => `Update ${n} row${n === 1 ? "" : "s"}`,
    applied: (n: number, label: string) => `${label}: ${n} updated.`,
    historyTitle: "Shape updates",
    historyRow: (r: { reshaped: number; renamed: number; moved: number }) =>
      `${r.reshaped} reshaped · ${r.renamed} renamed · ${r.moved} moved`,
    undone: "undone",
    undo: "Undo",
    undoTitle: "Undo this shape update?",
    undoBody: "Shapes, names and parents go back to what they were before this update, and field data follows any block or ward moved back. Field data itself is not touched.",
    failed: "Couldn't update shapes.",
  },

  propertyImportPanel: {
    panelTitle: (total: number) => (total ? `Property outlines · ${total}` : "Property outlines"),
    dropHint: "Drop a GeoJSON, or click to browse",
    importingFile: (name: string) => `Importing ${name}…`,
    reuploadHint: "Re-uploading adds only what is new",
    onlyGeojsonAccepted: "Only .geojson / .json files are accepted.",
    importFailed: "Import failed.",
    couldntUndoUpload: "Couldn't undo that upload.",
    couldntDeleteOutlines: "Couldn't delete the outlines.",
    serverRefusedDelete: "The server refused to delete the outlines.",
    createdAdded: (count: number) => `${count} added`,
    alreadyPresent: (count: number) => `, ${count} already present`,
    outsideEveryBlock: (count: number) => `, ${count} outside every block`,
    everythingAlreadyImported: "Everything in this file was already imported — nothing changed.",
    reprojectedFrom: (crs: string) => `Reprojected from ${crs}.`,
    unplacedHint:
      "Outlines outside every block cannot be surveyed — import the blocks that cover them, then upload this file again.",
    numberedInField: "Numbered in the field",
    numberedOfTotal: (done: number, total: number) => `${done} of ${total}`,
    propertiesNumberedLabel: "Properties numbered",
    loadingUploads: "Loading…",
    noOutlinesYetTitle: "No property outlines yet",
    noOutlinesYetBody:
      "Upload a GeoJSON of building footprints. They appear on the surveyor's map ready to number, so nothing has to be traced by hand.",
    noUploadsRecordedFor: (total: number) =>
      `No uploads recorded for these ${total} outline${total === 1 ? "" : "s"}. They may predate this feature, or were created directly in the field rather than imported.`,
    uploadsTitle: "Uploads",
    propertyImportFallback: "Property import",
    remainingRowsTitle: "Rows from this upload that still exist",
    undoOfferNote: "Undo is offered for what you uploaded just now, and only until you leave this page. After that, use Delete all outlines.",
    deleteAllOutlines: "Delete all outlines",
    exportedCanVerb: (verb: string) => `Exported. You can now ${verb}.`,
    exportFieldDataFirst: "Export the field data first.",
    verbUndo: "undo",
    verbDelete: "delete",
    verbContinue: "continue",
    undoThisUpload: (name: string) => `Undo ${name}?`,
    thisUpload: "this upload",
    undoBody: (count: number) =>
      `Removes the ${count} property outline(s) this upload created, leaving every other upload in place. You would upload the file again to get them back.`,
    undoImportLabel: "Undo import",
    destroysFieldWorkTitle: "This would destroy collected field work",
    destroysFieldWorkBody: (count: number) =>
      `${count} of these properties`,
    destroysFieldWorkBodySuffix:
      "have already been given a property number by a surveyor. Undoing the upload deletes them — the outline can be re-imported, the number cannot.",
    deleteAnyway: "Delete anyway",
    deleteAllQuestion: (total: number) => `Delete all ${total} property outline(s)?`,
    deleteAllBodyPrefix: "Every outline imported into this project is removed, from every upload — including the",
    deleteAllBodySuffix: "already numbered by surveyors. Shapes drawn by hand in the field are not touched.",
    continueLabel: "Continue",
    lastChanceTitle: "Last chance — this cannot be undone",
    permanentlyDeleting: (total: number) => `Permanently deleting`,
    outlineCount: (total: number) => `${total} outline(s)`,
    ofWhich: ", of which",
    carryHouseNumber:
      "carry a house number a surveyor assigned in the field. Uploading the file again brings the outlines back, but blank — those numbers cannot be recovered from anything the office holds.",
    noneNumberedYet: ". None have been numbered yet, so no field work is lost.",
    deletePermanently: "Delete permanently",
  },

  rasterPanel: {
    panelTitle: (count: number) => (count ? `Drone imagery · ${count}` : "Drone imagery"),
    dropHint: "Drop a GeoTIFF here, or click to browse",
    uploadingFile: (name: string) => `Uploading ${name}…`,
    transferComplete: "Transfer complete — saving on the server…",
    percentUploaded: (pct: number) => `${pct}% uploaded`,
    convertedHint: "Converted to a cloud-optimised GeoTIFF automatically after upload",
    onlyTiffAccepted: "Only .tif / .tiff files are accepted.",
    uploadFailed: "Upload failed.",
    couldntAddFile: "Could not add this file.",
    deleteFailed: "Delete failed.",
    retryFailed: "Retry failed.",
    couldntReadLocation: "Couldn't read this imagery's location from TiTiler.",
    cogTifArrow: "_cog.tif →",
    copyFolderPath: "Copy this folder path",
    foundOnServer: "Found on the server · adding skips conversion",
    unusable: "Unusable",
    fileMissing: "File missing",
    fileMissingBody:
      "The file is no longer on the server — it was deleted or moved outside the portal. Delete this entry, then re-upload or re-add the file.",
    blockSpecific: "Block-specific",
    projectWide: "Project-wide",
    approxConverted: (pct: number) => `~${pct}% converted`,
    converting: "Converting…",
    noImageryYetTitle: "No imagery yet",
    noImageryYetBody:
      "Upload an orthomosaic for this project and it becomes available to every block beneath it.",
    zoomTo: "Zoom to",
    retry: "Retry",
    deleteThisImageryTitle: "Delete this imagery?",
    deleteImageryBodySuffix:
      "and its converted tiles are removed from the server. Undoing this means re-uploading the original file and waiting for conversion again — often several GB and many minutes. Blocks and collected shapes are not affected.",
    deleteImageryConfirmLabel: "Delete imagery",
  },

  layerRowsTable: {
    badge: "Editable layer",
    badgeTitle: (name: string) =>
      `Shape from the editable layer “${name}”: reference data, not a surveyed property. Read-only here.`,
    layerShapes: (n: string) => `${n} layer shapes`,
    loadFailed: "Couldn't load the editable-layer shapes.",
  },
  featureTable: {
    panelTitle: (count: number) => (count ? `Field data · ${count}` : "Field data"),
    rowsEdited: (count: number) => `${count} row${count === 1 ? "" : "s"} edited`,
    discard: "Discard",
    saving: "Saving…",
    save: "Save",
    saveOrDiscardFirst: "Save or discard your edits first",
    done: "Done",
    edit: "Edit",
    preparing: "Preparing…",
    exportShapefile: "Export Shapefile",
    noShapesYetTitle: "No shapes collected yet",
    noShapesYetBody:
      "Points and building outlines recorded by surveyors in the field will appear here as they sync.",
    colType: "Type",
    colZone: "Zone",
    colWard: "Ward",
    colBlock: "Block",
    colPropertyNo: "Property no.",
    searchPlaceholder: "Search property no.",
    clearSearch: "Clear search",
    noMatchTitle: "No matching property",
    noMatchBody: (column: string, q: string) => `No property has ${column} matching "${q}" in this view.`,
    tabFieldData: "Field data",
    tabLayers: "Editable layers",
    tabAll: "All",
    noMatchAnyBody: (q: string) => `Nothing matches "${q}" in this view.`,
    finishEditingFirst: "Finish editing first",
    searchColumnTitle: "Column to search",
    searchIn: (column: string) => `Search ${column}`,
    allColumns: "All columns",
    colCreatedBy: "Created by",
    noLayerMatchTitle: "No matching layer shape",
    noLayerMatchBody: "No editable-layer shape matches these filters in this view.",
    noLayerShapesTitle: "No layer shapes here",
    noLayerShapesBody: "No editable-layer shape lies in this area.",
    allSurveyors: "All surveyors",
    noSurveyorMatchTitle: "Nothing by this surveyor",
    noSurveyorMatchBody: (name: string) => `${name} has collected nothing, and changed no layer shape, in this view.`,
    surveyorFilterTitle: "Survey properties collected by this surveyor; editable-layer shapes last changed by them",
    dateFrom: "From",
    dateTo: "To",
    clearDates: "Clear dates",
    dateFilterTitle: "Survey properties by the date they were created; editable-layer shapes by the date they were last updated",
    noDateMatchTitle: "Nothing in these dates",
    noDateMatchBody: "No property was created, and no layer shape updated, between these dates in this view.",
    colMarkRevisit: "Mark as revisit",
    markRevisitTitle: "Mark this property for a revisit (drawn white on the map)",
    revisitOnly: "Marked for revisit",
    revisitOnlyTitle: "Show only the properties marked for a revisit",
    noRevisitTitle: "No property marked for revisit",
    noRevisitBody: "Tick \"Mark as revisit\" on a row to add it to this list.",
    revisitFailed: "Couldn't change the revisit mark.",
    colPartition: "Partition",
    colPlotNo: "Plot no.",
    colRecorded: "Recorded",
    colActions: "Actions",
    propertyNumberAriaLabel: (id: number) => `Property number, feature ${id}`,
    partitionAriaLabel: (id: number) => `Partition, feature ${id}`,
    plotNumberAriaLabel: (id: number) => `Plot number, feature ${id}`,
    columnAriaLabel: (col: string, id: number) => `${col}, feature ${id}`,
    zoomToShapeTitle: "Zoom to this shape on the map",
    noGeometryTitle: "This row has no geometry to zoom to",
    zoomToAriaLabel: (label: string) => `Zoom to ${label}`,
    thisShape: "this shape",
    removing: "Removing…",
    delete: "Delete",
    deleteThisShapeTitle: "Delete this shape?",
    propertyNumberPrefix: "Property number",
    thisGeometry: (type: string) => `This ${type} `,
    thisShapeFallback: "shape",
    deleteShapeBodySuffix:
      "was collected in the field and cannot be recovered — someone would have to return to the property and record it again.",
    deleteShapeConfirmLabel: "Delete shape",
    savedThenStopped: (saved: number, detail: string) =>
      `Saved ${saved} row${saved === 1 ? "" : "s"}, then stopped: ${detail}`,
    couldntSaveChanges: "Couldn't save those changes.",
    couldntDeleteShape: "Couldn't delete that shape.",
    exportFailed: "Export failed.",
  },

  editableLayersPanel: {
    panelTitle: (count: number) => (count ? `Editable layers · ${count}` : "Editable layers"),
    dropHint: "Drop a GeoJSON, or click to browse",
    uploadingFile: (name: string) => `Uploading ${name}…`,
    uploadHint: "Roads, drainage, plan overlays — edited in the field app",
    onlyGeojsonAccepted: "Only .geojson / .json files are accepted.",
    uploadFailed: "Upload failed.",
    couldntSaveChanges: "Couldn't save those changes.",
    loadingUploads: "Loading…",
    noLayersYet:
      "No editable layers yet. Anything uploaded here draws on the map beside the survey data without becoming part of it, and surveyors can correct it in the field app.",
    hideLayer: "Hide this layer",
    showLayer: "Show this layer",
    actionsFor: (name: string) => `Actions for ${name}`,
    hideLayerMenu: "Hide layer",
    showLayerMenu: "Show layer",
    zoomToLayer: "Zoom to layer",
    viewTable: "View table",
    export: "Export",
    deleteLayer: "Delete layer",
    deleteLayerTitle: "Delete this layer?",
    deleteLayerBodySuffix: (count: string) =>
      `and its ${count} features will be removed, including any corrections made in the field. No survey data is affected — this layer is reference geometry only.`,
    deleteLayerConfirmLabel: "Delete layer",
    updateFromFile: "Update from file…",
    mergeTitle: (name: string) => `Update ${name}`,
    mergeIntro: (file: string) =>
      `From ${file}: new shapes are added, shapes already in the layer are skipped, and reshaped ones get their new outline. Nothing is removed.`,
    mergeKey: "Match shapes by",
    mergeNoKey: "Outline only (no ID attribute)",
    mergeAdded: "new shapes will be added",
    mergeReshaped: "shapes will get a new outline",
    mergeAttrs: "shapes will gain attributes they don't have yet",
    mergeUnchanged: "already in the layer, skipped",
    mergeSkipped: "skipped (repeated or unclear ID, or no usable geometry)",
    mergeNote: (count: string) =>
      `The layer will have ${count} shapes. Attribute values already in the layer (including field corrections) are kept.`,
    mergeApply: "Update layer",
    mergeDone: (name: string, added: number, reshaped: number) =>
      `${name} updated: ${added} added, ${reshaped} reshaped.`,
  },

  editableLayerTableDialog: {
    attributeTableFallback: "Attribute table",
    loading: "Loading…",
    featureCount: (countText: string, rawCount: number) => `${countText} feature${rawCount === 1 ? "" : "s"}`,
    propertyCount: (count: number) => ` · ${count} propert${count === 1 ? "y" : "ies"}`,
    rowsEditedCount: (count: number) => ` · ${count} row${count === 1 ? "" : "s"} edited`,
    loadingAttributes: "Loading attributes…",
    noFeatures: "This layer has no features.",
    noProperties: (countText: string) => `These ${countText} features carry no properties.`,
    saveRowHeader: "Save row",
    saveRowTitle: (row: number) => `Save row ${row} only, leaving other edited rows pending`,
    nothingEditedRow: "Nothing edited in this row",
    discard: "Discard",
    saveAllRows: (count: number) => `Save all ${count} row${count === 1 ? "" : "s"}`,
    saving: "Saving…",
    done: "Done",
    saveOrDiscardFirst: "Save or discard your edits first",
    edit: "Edit",
    cellAriaLabel: (col: string, row: number) => `${col}, row ${row}`,
    rowRange: (from: string, to: string, total: string) => `${from}–${to} of ${total}`,
    firstPage: "First page",
    previousPage: "Previous page",
    nextPage: "Next page",
    lastPage: "Last page",
    rowsPerPage: "Rows per page",
    perPage: (count: number) => `${count} / page`,
  },

  scopeTree: {
    intro: "Tick any level to assign. A tick covers everything beneath it, including rows imported later.",
    loadingHierarchy: "Loading projects and hierarchy…",
    noProjectsToAssign: "No projects to assign.",
    nothingImportedYet: "Nothing imported at this level yet.",
    collapse: "Collapse",
    expand: "Expand",
    alreadyCovered: "Already covered by an assignment higher up",
    partialHint: "Something inside this is assigned. Tick to assign the whole thing.",
    partial: "partial",
    levelProject: "Project",
    levelZone: "Zone",
    levelWard: "Ward",
    levelBlock: "Block",
    nothingAssigned: "Nothing assigned — this account will see nothing.",
    assignmentsCount: (count: number) => `${count} assignment${count === 1 ? "" : "s"}`,
    cancel: "Cancel",
    save: "Save",
    couldntLoadProjects: "Couldn't load projects.",
    couldntLoadLevel: "Couldn't load that level.",
  },

  hierarchyFilterBar: {
    allZones: "All zones",
    allWardsPlaceholder: "All wards",
    allBlocksPlaceholder: "All blocks",
    selectWardFirst: "Select a ward first",
    allWards: (count: number) => `All wards (${count})`,
    allBlocks: (count: number) => `All blocks (${count})`,
    clear: "Clear",
    selectedOnly: "Selected only",
    selectedOnlyTitle: "On: the map shows only the selected zone, ward or block. Off: the map shows everything.",
    shownOfTotal: (shown: string, total: string) => `${shown} of ${total} in`,
  },

  assignmentProgressDialog: {
    assignmentProgress: "Assignment progress",
    workingItOut: "Working it out…",
    couldntLoadProgress: "Couldn't load progress.",
    adminNote:
      "This is an administrator. It can reach every project, so it holds no assignment to track.",
    nothingAssignedYetPrefix: "Nothing assigned yet. Use",
    assignmentWord: "Assignment",
    nothingAssignedYetSuffix:
      "to give this account a project, zone, ward or block to work in.",
    blocksAssigned: (count: number) => `${count} block${count === 1 ? "" : "s"} assigned`,
    percentDone: (pct: number | null) => `${pct}% done`,
    submittedLabel: "submitted",
    inProgressLabel: "in progress",
    notStartedLabel: "not started",
    propertiesInBlocksTitle: "Properties in those blocks",
    numberedOfTotal: (numbered: string, total: string) => `of ${total} numbered`,
    stillToDo: (count: string) => `${count} still to do`,
    byProject: "By project",
    blocksFraction: (submitted: number, total: number) => `${submitted}/${total} blocks`,
    collectedBySurveyor: "Collected by this surveyor",
    properties: "Properties",
    polygons: "Polygons",
    pinned: (count: number) => `+${count} pinned`,
    today: "Today",
    yesterday: "Yesterday",
    nothingCollectedYet:
      "Nothing collected under this account yet. A property counts here once it has been given a house number in the field.",
    months: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
  },

  newProjectForm: {
    newProject: "New project",
    projectNameLabel: "Project name",
    projectNamePlaceholder: "Thergaon Zone 4 — 2026 resurvey",
    descriptionLabel: "Description",
    optional: "(optional)",
    descriptionPlaceholder: "Ward 12 boundary resurvey",
    creating: "Creating…",
    create: "Create",
    cancel: "Cancel",
    couldntCreateProject: "Couldn't create the project.",
  },

  slidePanel: {
    closePanel: "Close panel",
  },

  mapPreview: {
    boundaryZones: "Zones",
    boundaryWards: "Wards",
    boundaryBlocks: "Blocks",
    imagery: "Imagery",
    fieldData: "Field data",
    propertyNumbers: "Property numbers",
    collapseBoundaries: "Collapse Boundaries",
    expandBoundaries: "Expand Boundaries",
    showHideAllBoundaries: "Show or hide all boundary levels",
    boundaries: "Boundaries",
    view: "View",
    satelliteMap: "Satellite map",    highlightProgress: "Highlight progress",
    inProgress: "In progress",
    submitted: "Submitted",
    notStarted: "Not started",
    zoneWardBlockNames: "Zone / ward / block names",
    boundaryLabels: "Labels",
    propertyNumbersOnLayers: "Property numbers on layers",
    plotNumbersOnLayers: "Plot numbers on layers",
    fieldDataOutlineOnly: "Field data: boundaries only",
    layersOutlineOnly: "Layers: boundaries only",
    outlineOnlyHint: "On: shapes drawn as outlines with no fill, so the map shows through. Off: filled as usual.",
    zoomInForNumbers: "Zoom in to see property numbers",
    plotPrefix: "Plot",
    noNumber: "No number",
    byUser: (name: string) => `by ${name}`,
    couldntLoadLayer: "Couldn't load this layer",
    markedForRevisit: "Marked for revisit",
    qaCheck: "Overlap & sliver check",
    qaOverlaps: "Overlaps",
    qaSlivers: "Slivers",
    legend: "Map legend",
  },
  workspace: {
    filtersAndLayers: "Filters & Layers",
    collapsePanel: "Hide the Filters & Layers panel",
    openPanel: "Show the Filters & Layers panel",
    filterTab: "Filter",
    layerTab: "Layer",
    zone: "Zone",
    ward: "Ward",
    block: "Block",
    noHierarchy: "No zones, wards or blocks have been imported for this project yet.",
    selectedAreaOnly: "Show selected area only",
    selectedAreaOnlyHint: "On: the map shows only the chosen zone, ward or block. Off: the map shows everything; the table stays filtered.",
    allOfProject: (n: string) => `All ${n} properties in this project.`,
    resetFilters: "Reset all filters",
    baseMap: "Base map",
    street: "Street",
    satellite: "Satellite",
    none: "None",
    drone: "Drone",
    boundaries: "Boundaries",
    showAll: "Show all",
    hideAll: "Hide all",
    surveyData: "Survey data",
    numbersHint: "Property numbers appear when zoomed in close",
    manage: "Manage",
    noEditableLayers: "No editable layers uploaded.",
    analysis: "Analysis",
    manageUploads: "Manage uploads",
    manageUploadsHint: "Drone imagery, property outlines and editable layers for this project.",
    surveyorSubtitle: "Field data for your assigned areas.",
    cardProperties: "Properties",
    cardNumbered: "Numbered",
    cardSubmitted: "Blocks submitted",
    cardInProgress: "Blocks in progress",
    cardNotStarted: "Blocks not started",
    cardImagery: "Imagery ready",
    cardRevisit: "Marked for revisit",
    ofTotal: (n: string) => `of ${n}`,
    ofBlocks: (n: string) => `of ${n}`,
    fieldDataTitle: (n: string) => `Field data · ${n}`,
    showTable: "Show table",
    hideTable: "Hide table",
    dragToResize: "Drag to resize",
    maximiseTable: "Give the table more room",
    restoreTable: "Restore the map",
  },
  surveyTimingPage: {
    title: "Survey Timing",
    filterFrom: "From",
    filterTo: "To",
    filterZone: "Zone",
    filterWard: "Ward",
    allZones: "All zones",
    allWards: "All wards",
    apply: "Apply",
    clear: "Clear",
    statPropertiesSurveyed: "Properties Surveyed",
    statBlocksSubmitted: "Blocks Submitted",
    statActiveSurveyors: "Active Surveyors",
    tabByUser: "By Surveyor",
    tabByBlock: "By Block",
    tabByWard: "By Ward",
    panelByUser: (n: string) => `By Surveyor (${n})`,
    panelByBlock: (n: string) => `By Block (${n})`,
    panelByWard: (n: string) => `By Ward (${n})`,
    noSurveysTitle: "No surveys recorded",
    noSurveysBody: "No properties have been numbered yet in this scope.",
    noBlocksTitle: "No blocks found",
    noBlocksBody: "No blocks exist in this scope.",
    noWardsTitle: "No wards found",
    noWardsBody: "No wards exist in this scope.",
    colSurveyor: "Surveyor",
    colPropertiesSurveyed: "Properties Surveyed",
    colFirstSurvey: "First Survey",
    colLastSurvey: "Last Survey",
    colAvgPropertyDuration: "Avg Property Duration",
    colBlock: "Block",
    colWard: "Ward",
    colStatus: "Status",
    colClaimed: "Claimed",
    colSubmitted: "Submitted",
    colBlockDuration: "Block Duration",
    colAvgPerProperty: "Avg / Property",
    colProperties: "Properties",
    colSurveyed: "Surveyed",
    colZone: "Zone",
    colBlocksTotal: "Blocks Total",
    colSurveyors: "Surveyors",
    statusPending: "Pending",
    statusInProgress: "In Progress",
    statusSubmitted: "Submitted",
    durationSeconds: (n: number) => `${n}s`,
    durationMinutes: (n: number) => `${n}m`,
    durationHoursMinutes: (h: number, m: number) => `${h}h ${m}m`,
    durationHours: (h: number) => `${h}h`,
  },
  accountSecurity: {
    button: "Phone & session",
    title: "Phone & session",
    description: "Registered phone, sign-in sessions and lockout for",
    loading: "Loading…",
    // row badges
    badgeOnline: "Online now",
    badgeSignedIn: "Signed in",
    badgeLastSeen: (when: string) => `Last seen ${when}`,
    badgeNeverSignedIn: "Never signed in",
    badgeNoPhone: "No phone registered",
    badgeLocked: (min: number) => `Locked ${min} min`,
    badgeLateUploads: (n: number) => `${n} late upload${n === 1 ? "" : "s"}`,
    // device
    deviceHeading: "Registered phone",
    deviceNotBound: "Office accounts are not tied to a phone.",
    deviceNone: "No phone registered. The next sign-in from the field app registers that phone.",
    deviceModel: "Phone",
    deviceId: "Device ID",
    deviceAndroid: "Android",
    deviceApp: "App version",
    deviceRegistered: "Registered",
    deviceLastLogin: "Last sign-in",
    resetDevice: "Reset phone…",
    resetDeviceTitle: "Reset the registered phone?",
    resetDeviceBody: (user: string) =>
      `${user} is signed out now and the phone is released. The next sign-in from the field app — on any phone — registers that phone instead. Use this when a phone is replaced, lost or broken.`,
    resetDeviceConfirm: "Reset phone",
    previousDevices: "Previous phones",
    releasedBy: (when: string, by: string) => `released ${when}${by ? ` by ${by}` : ""}`,
    // sessions
    sessionHeading: "Sessions",
    sessionNone: "No sign-ins yet.",
    sessionActive: "Active",
    sessionEnded: "Ended",
    sessionExpired: "Expired",
    sessionStarted: "Started",
    sessionLastActivity: "Last activity",
    sessionIp: "IP address",
    sessionDevice: "Device",
    sessionBrowser: "Browser (portal)",
    endReason: {
      logout: "signed out",
      replaced: "signed in again",
      force_logout: "forced logout",
      device_reset: "phone reset",
      password_reset: "password changed",
      idle: "inactive too long",
      device_blocked: "phone blocked",
      account_disabled: "account disabled",
      session_revoked: "ended by office",
    } as Record<string, string>,
    finalUploadPending: "may still upload offline work",
    finalUploadDone: "final upload done",
    forceLogout: "Force logout…",
    forceLogoutTitle: "Force logout?",
    forceLogoutBody: (user: string) =>
      `${user} is signed out everywhere, on their very next action. They can sign in again with their password.`,
    forceLogoutConfirm: "Force logout",
    noActiveSession: "Not signed in anywhere right now.",
    allowFinalSync: "Let the phone upload work it collected offline first",
    allowFinalSyncHint:
      "Recommended. Uploads made after the logout are marked for review below. Untick for a lost or stolen phone.",
    // lockout
    lockHeading: "Sign-in lock",
    lockedFor: (min: number) =>
      `Locked after too many wrong passwords — ${min} more minute${min === 1 ? "" : "s"}.`,
    notLocked: "Not locked.",
    failedSoFar: (n: number) => `${n} wrong password${n === 1 ? "" : "s"} in a row.`,
    unlock: "Unlock now",
    // late uploads
    lateHeading: "Uploaded after the session was ended",
    lateIntro:
      "Properties a phone sent after the office signed it out (its one final upload). Check them before relying on them.",
    lateNone: "None.",
    lateProperty: "Property",
    lateBlock: "Block",
    lateAction: "Change",
    lateWhen: "When",
    lateDeleted: "since deleted",
    lateActions: { create: "added", update: "edited", delete: "deleted" } as Record<string, string>,
    lateMore: (shown: number, total: number) => `Showing the latest ${shown} of ${total}.`,
    // notices
    noticeLoggedOut: (user: string) => `${user} has been signed out.`,
    noticeDeviceReset: (user: string) => `${user}'s phone was reset. Their next sign-in registers a phone.`,
    noticeUnlocked: (user: string) => `${user} is unlocked.`,
    // approval, blocking, disabling
    badgePending: "Phone waiting for approval",
    badgeDisabled: "Disabled",
    deviceNoneApproval: "No phone registered. A phone that signs in from the field app waits here for approval.",
    pendingHeading: "Phones waiting for approval",
    pendingReplaces: (model: string) => `Approving replaces ${model}`,
    pendingLastTry: (when: string) => `last tried ${when}`,
    approve: "Approve",
    reject: "Reject",
    rejectBlock: "Reject & block",
    noticeApproved: (user: string) => `Phone approved for ${user}. They can sign in now.`,
    noticeRejected: (user: string) => `Phone turned down for ${user}.`,
    blockDevice: "Block phone…",
    blockDeviceTitle: "Block this phone?",
    blockDeviceBody: (user: string) =>
      `${user} is signed out now and this phone cannot sign in to any account until it is unblocked (Phones & sessions page). A replacement phone can then be registered to ${user}. Use this for a lost or stolen phone.`,
    blockDeviceConfirm: "Block phone",
    noticeDeviceBlocked: (user: string) => `${user}'s phone is blocked.`,
    endSession: "End",
    endSessionTitle: "End this session?",
    endSessionBody: (user: string) =>
      `This one sign-in of ${user} is closed on its next action. Their other sessions stay signed in.`,
    endSessionConfirm: "End session",
    noticeSessionEnded: "Session ended.",
    sessionLimit: (n: number) =>
      n === 0
        ? "No limit on sessions at once."
        : `Up to ${n} session${n === 1 ? "" : "s"} at once; signing in again closes the oldest.`,
    accountHeading: "Account access",
    accountActive: "Active. The account can sign in.",
    accountDisabledText: (by: string, when: string) =>
      `Disabled${by ? ` by ${by}` : ""} ${when}. Sign-ins are refused until it is enabled again.`,
    disable: "Disable account…",
    enable: "Enable account",
    disableTitle: "Disable this account?",
    disableBody: (user: string) =>
      `${user} is signed out everywhere and cannot sign in until the account is enabled again. Nothing is deleted.`,
    disableConfirm: "Disable account",
    noticeDisabled: (user: string) => `${user} is disabled.`,
    noticeEnabled: (user: string) => `${user} is enabled again.`,
    close: "Close",
  },
  sessionsPage: {
    crumb: "Phones & sessions",
    heading: "Phones & sessions",
    subheading: "Who is signed in right now, phones waiting for approval, and blocked phones.",
    refresh: "Refresh",
    pendingHeading: "Phones waiting for approval",
    pendingNone: "No phone is waiting.",
    activeHeading: "Signed in now",
    activeNone: "Nobody is signed in.",
    blockedHeading: "Blocked phones",
    blockedNone: "No phone is blocked.",
    colAccount: "Account",
    colDevice: "Device",
    colStarted: "Signed in",
    colLastActivity: "Last activity",
    colIp: "IP address",
    colReason: "Reason",
    colBlocked: "Blocked",
    idleFor: (min: number) => (min < 1 ? "active now" : `idle ${min} min`),
    unblock: "Unblock",
    noticeUnblocked: (model: string) => `${model} is unblocked.`,
    blockedBy: (when: string, by: string) => `${when}${by ? ` by ${by}` : ""}`,
  },
  blockControls: {
    claimedBy: (name: string, when: string) => `Claimed by ${name} since ${when}`,
    lastActivity: (when: string) => `last activity ${when}`,
    expires: (when: string) => `claim lapses ${when} if idle`,
    notClaimed: "Not claimed by anyone",
    release: "Release",
    assign: "Assign",
    reassign: "Reassign",
    history: "History",
    releaseTitle: "Release this block?",
    releaseBody: (block: string, holder: string) =>
      `${holder} will no longer hold ${block}, and any surveyor assigned to it can claim it. Everything already uploaded stays on the block under the name of whoever collected it, and work still on ${holder}'s phone can still upload.`,
    reassignTitle: "Give this block to another surveyor",
    reassignBody: (block: string) =>
      `The surveyor you choose holds ${block} from now on. Everything already uploaded stays on the block under the name of whoever collected it, and work still on the previous surveyor's phone can still upload.`,
    surveyor: "Surveyor",
    chooseSurveyor: "Choose a surveyor…",
    reopenTitle: "Send back to surveyor",
    reopenBody:
      "The block opens again for corrections until the time below, then locks by itself. It also locks again as soon as it is resubmitted. You are recorded as the approving authority.",
    reopenFor: "Who corrects it",
    reopenForHolder: (name: string) => `${name} (who held it)`,
    reopenForNobody: "Nobody — any assigned surveyor can claim it",
    reasonLabel: "Reason",
    reasonPlaceholder: "What has to be corrected, and why",
    reasonHint: "Required, at least 10 characters.",
    validUntilLabel: "Open until",
    validUntilHint: "Required. After this the block locks again, even if it was not resubmitted.",
    reopenedUntil: (when: string, by: string) => `Reopened for corrections until ${when}, approved by ${by}.`,
    historyTitle: (block: string) => `History of ${block}`,
    historyNone: "Nothing recorded yet.",
    by: (name: string) => `by ${name}`,
    eventLabel: (e: string) =>
      ({
        claim: "Claimed",
        claim_resumed: "Claim resumed",
        claim_reopened: "Claimed for corrections",
        claim_expired: "Claim lapsed (inactive)",
        release: "Released",
        reassign: "Reassigned",
        submit: "Submitted",
        resubmitted: "Resubmitted — locked again",
        reopen_requested: "Reopen requested",
        reopen_approved: "Reopen approved",
        reopen_rejected: "Reopen rejected",
        relocked_expired: "Locked again — reopen time ran out",
        paired: "Paired with another block",
        unpaired: "Pairing ended",
      })[e] ?? e,
    pairedWith: (block: string) => `Worked together with ${block}`,
    pairedHint: "Shapes may cross the line between the two, house numbers are shared, and submitting one submits both.",
    endPair: "End pairing",
    endPairTitle: "End this pairing?",
    endPairBody: (a: string, b: string) =>
      `${a} and ${b} go back to being surveyed separately. Both stay with the same surveyor, and every shape stays in the block it is filed under.`,
    noticePairEnded: (a: string, b: string) => `${a} and ${b} are no longer paired.`,
    noticeReleased: (block: string) => `${block} is released.`,
    noticeReassigned: (block: string, user: string) => `${block} now belongs to ${user}.`,
    noticeReopened: (block: string, until: string) => `${block} is open for corrections until ${until}.`,
  },
  reopenRequestsPage: {
    crumb: "Reopen requests",
    heading: "Reopen requests",
    subheading:
      "Surveyors ask to correct a submitted block. Approving needs an end time; rejecting needs a reason. A reopened block locks again when it is resubmitted or when its time runs out.",
    refresh: "Refresh",
    pendingHeading: "Waiting for a decision",
    pendingNone: "No request is waiting.",
    historyHeading: "Earlier requests",
    historyNone: "No earlier requests.",
    colBlock: "Block",
    colRequestedBy: "Asked by",
    colReason: "Reason",
    colStatus: "Outcome",
    colDecidedBy: "Decided by",
    colValidUntil: "Open until",
    approve: "Approve",
    reject: "Reject",
    approveTitle: (block: string) => `Reopen ${block}?`,
    approveBody: (who: string) =>
      `${who} gets the block back to correct it until the time below. You are recorded as the approving authority.`,
    rejectTitle: (block: string) => `Reject the request for ${block}?`,
    rejectBody: (who: string) => `${who} sees your reason in the app. The block stays locked.`,
    rejectNote: "Reason for rejecting",
    noteOptional: "Note to the surveyor (optional)",
    forUser: (name: string) => `for ${name}`,
    noticeApproved: (block: string) => `${block} is reopened.`,
    noticeRejected: (block: string) => `Request for ${block} rejected.`,
    status: {
      pending: "Waiting",
      approved: "Open now",
      rejected: "Rejected",
      expired: "Time ran out — locked again",
      completed: "Resubmitted — locked again",
    } as Record<string, string>,
  },
  idle: {
    stillThereTitle: "Are you still there?",
    stillThereBody: (min: number, left: string) =>
      `No activity for ${min} minutes. You will be signed out in ${left}.`,
    cancel: "Cancel — stay signed in",
    signOutNow: "Sign out",
    signedOut: (min: number) => `You were signed out after ${min} minutes without activity. Sign in again.`,
    timedOutTitle: "Session timed out",
    ok: "OK",
  },
};

export type Translations = typeof en;
